import { text } from "../utils/i18n";
import { BooleanOrAny } from "./BooleanOrAny";
import { DateStrategy } from "./DateStrategy";
import OrderTag from "./OrderTag";
import { Products } from "./Products";
import Sort from "./Sort";

export type ListOrderFilterDescriptionsParams = {
  orderTags: OrderTag[];
}

/**
 * Filters for orders.
 */
export default class OrderFilters {
  static readonly maxCount: number = 100;

  readonly query?: string;
  /**
   * Minimum value of either `date` or `businessDate`, determined by `dateStrategy`.
   */
  readonly from?: Date;
  /**
   * Maximum value of either `date` or `businessDate`, determined by `dateStrategy`.
   */
  readonly to?: Date;
  /**
   * Minimum value of `date`.
   */
  readonly createdAfter?: Date | RelativeDate;
  /**
   * Maximum value of `date`.
   */
  readonly createdBefore?: Date | RelativeDate;
  readonly dateStrategy?: DateStrategy = DateStrategy.BusinessDateFirst;
  readonly ids: string[] = [];

  /**
   * True to limit to orders with a negated tag.
   * False to limit to orders without a negated tag.
  */
  readonly anyNegatedTag?: boolean;
  readonly allTagIds: string[] = [];
  readonly anyTagId: string[] = [];
  readonly notAnyTagId: string[] = [];
  readonly noTag?: boolean;
  readonly notAllTagIds: string[] = [];

  readonly anyProductCategoryId: string[] = [];
  readonly allProductCategoryIds: string[] = [];
  readonly notAnyProductCategoryId: string[] = [];
  readonly notAllProductCategoryIds: string[] = [];

  readonly allProductIds: string[] = [];
  readonly anyProductId: string[] = [];
  readonly notAnyProductId: string[] = [];
  readonly notAllProductIds: string[] = [];

  readonly allFileNames: string[] = [];
  readonly anyFileName: string[] = [];
  readonly notAnyFileName: string[] = [];
  readonly notAllFileNames: string[] = [];

  readonly allFieldIds: string[] = [];
  readonly anyFieldId: string[] = [];
  readonly notAnyFieldId: string[] = [];
  readonly notAllFieldIds: string[] = [];

  readonly userIds: string[] = [];
  readonly teamIds: string[] = [];

  /**
   * Whether to exclude `userIds`.
   */
  readonly excludeUserIds?: boolean;
  readonly sort = new Sort({ asc: false, field: 'id' });
  readonly offset: number = 0;
  readonly count: number = 50;
  readonly prioritizeSalaryBasis?: boolean;
  readonly excludeVat?: boolean;
  readonly isTest: BooleanOrAny = BooleanOrAny.False;
  readonly projectIds: string[] = null; //  Default to any project for actions.
  readonly excludeProjectIds?: boolean;
  /**
   * Acts like `search`, but can expand the right scope, for viewing
   * a contact's associated orders.
   */
  readonly phoneNumbers: string[] = [];

  constructor(deriveFrom?: Partial<OrderFilters>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }
  }

  toQuery() {
    return {
      ...this,
      s: this.query,
      count: Math.min(this.count, OrderFilters.maxCount),
      sort: undefined,
      ...this.sort?.toQuery(),
    }
  }
}

export function listOrderFilterDescriptions(filters: Partial<OrderFilters>, orderTags: ById<OrderTag>, products: Products): string[] {
  const descriptions: string[] = [];

  /*
  //  Relative date descriptions first to not confuse the combination of dates and tags, etc.
  const beforeRelativeDescription = translateRelativeDate(
    filters.createdBefore as RelativeDate,
    "before"
  );

  const afterRelativeDescription = translateRelativeDate(
    filters.createdAfter as RelativeDate,
    "after"
  );

  if (beforeRelativeDescription) {
    descriptions.push(beforeRelativeDescription);
    
  } else if (afterRelativeDescription) {
    descriptions.push(afterRelativeDescription);
  }
    */

  if (filters.anyNegatedTag === true) {
    descriptions.push(text`ånger`);

  } else if (filters.anyNegatedTag === false) {
    descriptions.push(text`ej ånger`);

  } else {
    filters.allTagIds?.forEach(tagId => {
      const tag = orderTags[tagId];

      if (tag) {
        descriptions.push(tag.name);
      }
    });

    filters.anyTagId?.forEach(tagId => {
      const tag = orderTags[tagId];

      if (tag) {
        descriptions.push(tag.name);
      }
    });
  }

  filters.allProductIds?.forEach(productId => {
    const product = products.getProduct(productId);
    if (product) {
      descriptions.push(product.name);
    }
  });

  filters.anyProductCategoryId?.forEach(categoryId => {
    const category = products.getCategory(categoryId);
    if (category) {
      descriptions.push(category.name);
    }
  });

  return descriptions;
}


/**
 * @returns Whether any non-default filter is present, i.e. if the user has entered a filter. 
 */
export function isOrderFiltersEmpty(filters: Partial<OrderFilters> | null | undefined) {
  if (!filters) {
    return null;  //  Must return null in VariableList.
  }

  return !(
    filters.query || filters.from || filters.to || filters.allTagIds?.length ||
    filters.anyTagId?.length || filters.anyProductCategoryId?.length ||
    filters.allProductIds?.length || filters.userIds?.length || filters.teamIds?.length ||
    filters.ids?.length || (filters.isTest && filters.isTest !== BooleanOrAny.False) ||
    filters.anyFieldId?.length || filters.allFieldIds?.length || filters.notAnyFieldId?.length ||
    filters.notAllFieldIds?.length
  );
}